import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { setSessionToken } from "@/lib/auth";
import { useAuth } from "@/context/AuthContext";

/**
 * Handles the WorkOS AuthKit callback: extracts `code` from the URL query
 * string, exchanges it with the backend for a persistent session_token,
 * stores it, refreshes the AuthContext, then navigates home.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh, setUser } = useAuth();
  const [error, setError] = useState(null);
  // useRef (not useState) as required to guard against StrictMode double-fire
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const errParam = params.get("error");
        const errDesc = params.get("error_description");
        if (errParam) throw new Error(errDesc || errParam);
        if (!code) throw new Error("Missing code in callback URL");

        const r = await api.post("/auth/callback", { code });
        const { session_token, ...user } = r.data;
        setSessionToken(session_token);
        // Update context immediately (avoids a second /auth/me round-trip)
        setUser({
          user_id: user.user_id,
          email: user.email,
          name: user.name,
          picture: user.picture,
          is_admin: user.is_admin,
        });

        // Clean the URL and go home
        window.history.replaceState({}, document.title, "/");
        // Trigger context refresh for consistency (idempotent)
        await refresh();
        nav("/", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message || "Auth failed";
        if (detail === "disposable_email_not_accepted") {
          setError(
            "L'indirizzo email usato è un dominio usa-e-getta (temp-mail). " +
            "Per registrarti su Spectraloom usa un'email reale (Gmail, Outlook, " +
            "il tuo dominio aziendale, ecc.)."
          );
        } else {
          setError(detail);
        }
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-base)]" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-white">
        {error ? (
          <>
            <div className="text-[color:var(--meter-poor)] font-semibold">Errore login</div>
            <div className="text-sm text-[color:var(--fg-secondary)] max-w-md text-center">{error}</div>
            <button
              className="btn-ghost text-sm mt-2"
              onClick={() => (window.location.href = "/")}
            >
              Torna alla home
            </button>
          </>
        ) : (
          <>
            <Loader2 className="animate-spin" size={28} />
            <div className="tiny-caps">Accesso in corso…</div>
          </>
        )}
      </div>
    </div>
  );
}
